import { UserButton } from '@clerk/clerk-react'
import { AppBar, Box, Container, Toolbar } from '@mui/material'
import { useEffect, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import UnsavedChangesDialog from '@/components/ui/UnsavedChangesDialog'
import { canViewGPs, hasRestrictedNav } from '@/lib/permissions'
import Logo from '../ui/icons/Logo'

interface RootLayoutProps {
  children: ReactNode
}

export default function RootLayout({ children }: RootLayoutProps) {
  const location = useLocation()
  const selected = location.pathname
  const hideEntityNav = hasRestrictedNav()

  useEffect(() => {
    const name = location.pathname === '/' ? '| Dashboard' : location.pathname.split('/').join(' | ')
    window.document.title = `Support ${name}`
  }, [location.pathname])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position='sticky'
        sx={{
          bgcolor: 'white',
          borderRadius: 0
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 4 }}>
          {/* Logo */}
          <Link to='/' style={{ textDecoration: 'none' }}>
            <Logo />
          </Link>

          {/* Navigation */}
          <Box
            component='nav'
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            <Link
              to='/'
              style={{
                textDecoration: 'none',
                fontSize: '16px',
                lineHeight: '16px',
                transition: 'all 0.2s ease-in-out',
                color: selected === '/' ? '#1A1A1A' : '#737373',
                fontWeight: selected === '/' ? 700 : 500
              }}
            >
              Dashboard
            </Link>
            {!hideEntityNav && (
              <Link
                to='/users'
                style={{
                  textDecoration: 'none',
                  fontSize: '16px',
                  lineHeight: '16px',
                  transition: 'all 0.2s ease-in-out',
                  color: selected.startsWith('/users') ? '#1A1A1A' : '#737373',
                  fontWeight: selected.startsWith('/users') ? 700 : 500
                }}
              >
                Users
              </Link>
            )}
            {/* Patients stays visible for restricted CS roles so they can search by name */}
            <Link
              to='/patients'
              style={{
                textDecoration: 'none',
                fontSize: '16px',
                lineHeight: '16px',
                transition: 'all 0.2s ease-in-out',
                color: selected.startsWith('/patients') ? '#1A1A1A' : '#737373',
                fontWeight: selected.startsWith('/patients') ? 700 : 500
              }}
            >
              Patients
            </Link>
            {!hideEntityNav && (
              <>
                <Link
                  to='/orders'
                  style={{
                    textDecoration: 'none',
                    fontSize: '16px',
                    lineHeight: '16px',
                    transition: 'all 0.2s ease-in-out',
                    color: selected.startsWith('/orders') ? '#1A1A1A' : '#737373',
                    fontWeight: selected.startsWith('/orders') ? 700 : 500
                  }}
                >
                  Orders
                </Link>
                <Link
                  to='/med-requests'
                  style={{
                    textDecoration: 'none',
                    fontSize: '16px',
                    lineHeight: '16px',
                    transition: 'all 0.2s ease-in-out',
                    color: selected.startsWith('/med-requests') ? '#1A1A1A' : '#737373',
                    fontWeight: selected.startsWith('/med-requests') ? 700 : 500
                  }}
                >
                  Medication Requests
                </Link>
              </>
            )}
            {/* GPs stays visible for CS agents, who are also allowed to update them */}
            {canViewGPs() && (
              <Link
                to='/gps'
                style={{
                  textDecoration: 'none',
                  fontSize: '16px',
                  lineHeight: '16px',
                  transition: 'all 0.2s ease-in-out',
                  color: selected.startsWith('/gps') ? '#1A1A1A' : '#737373',
                  fontWeight: selected.startsWith('/gps') ? 700 : 500
                }}
              >
                GPs
              </Link>
            )}
            {!hideEntityNav && (
              <Link
                to='/products'
                style={{
                  textDecoration: 'none',
                  fontSize: '16px',
                  lineHeight: '16px',
                  transition: 'all 0.2s ease-in-out',
                  color: selected.startsWith('/products') ? '#1A1A1A' : '#737373',
                  fontWeight: selected.startsWith('/products') ? 700 : 500
                }}
              >
                Products
              </Link>
            )}
          </Box>

          {/* User Widget */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <UserButton
              appearance={{
                elements: {
                  userButtonBox: {
                    flexDirection: 'row-reverse'
                  }
                }
              }}
              showName
              afterSignOutUrl='/'
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container component='main' maxWidth={false} sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 2 }}>
        {children}
      </Container>
      <UnsavedChangesDialog />
    </Box>
  )
}
